import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Button,
  Typography,
  Card,
  Box,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  TextField as MuiTextField,
  FormHelperText,
  alpha,
  Autocomplete,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Page from '../../../components/Page';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { studentBehaviourService } from 'src/services/student-behaviour.service';
import { studentService } from 'src/services/student.service';
import { behaviourService, BehaviourType } from 'src/services/behaviourService';
import { useAuth } from 'src/context/auth/AuthContext';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { toast } from 'react-toastify';
import moment from 'moment';

interface StudentOption {
  label: string;
  value: string;
  email?: string;
  grade?: string;
}

interface FormValues {
  students: StudentOption[];
  behaviourTypeId: string;
  notes: string;
}

const validationSchema = Yup.object().shape({
  students: Yup.array().min(1, 'Please select at least one scholar').required('Scholar selection is required'),
  behaviourTypeId: Yup.string().required('Please choose a behavior type'),
  notes: Yup.string().required('Please provide notes or context for this record').max(500, 'Notes cannot exceed 500 characters'),
});

export default function AddBehaviour() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { getBaseRoute, hasPermission, user } = useAuth();

  const isEditMode = location.pathname.includes('/edit-student-behavior');
  const isDirectStudentMode = location.pathname.includes('/add-student-behaviour/') && Boolean(id);

  const canViewAll = hasPermission('street_cred.view_all');
  const teacherUserId = !canViewAll ? (user?.id || user?.email) : undefined;

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: 'S.T.R.E.E.T Cred', path: getBaseRoute('/street-cred') },
      { label: isEditMode ? 'Edit Behavior' : 'Log Scholar Behavior' },
    ],
  });

  // Fetch active students (scoped to teacher if not admin)
  const { data: studentsData = [], isLoading: isLoadingStudents } = useQuery({
    queryKey: ['students-options', teacherUserId],
    queryFn: async () => {
      const res = await studentService.getActiveStudents({
        teacherUserId,
      });
      return res.map((s: any) => ({
        label: s.name || `${s.user?.firstname || ''} ${s.user?.lastname || ''}`.trim() || 'Scholar',
        value: s.studentId || s.id,
        email: s.user?.email || '',
        grade: s.grade ? `Grade ${s.grade}` : '',
      }));
    },
  });

  // Fetch behavior types
  const { data: behaviourTypes = [], isLoading: isLoadingTypes } = useQuery({
    queryKey: ['behaviourTypes'],
    queryFn: () => behaviourService.getAllBehaviourTypes(),
  });

  // If edit mode, fetch existing behavior log
  const { data: existingBehaviour, isLoading: isLoadingExisting } = useQuery({
    queryKey: ['studentBehaviour', id],
    queryFn: () => (id ? studentBehaviourService.getStudentBehaviourById(id) : null),
    enabled: isEditMode && !!id,
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      students: [],
      behaviourTypeId: '',
      notes: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (isEditMode && id) {
          await studentBehaviourService.updateStudentBehaviour(id, {
            studentId: values.students[0]?.value,
            behaviourTypeId: values.behaviourTypeId,
            notes: values.notes,
          });
          toast.success('Behavior entry updated successfully');
        } else {
          const promises = values.students.map((student) =>
            studentBehaviourService.createStudentBehaviour({
              studentId: student.value,
              behaviourTypeId: values.behaviourTypeId,
              notes: values.notes,
              points: 0,
            })
          );
          await Promise.all(promises);
          toast.success(`Logged behavior for ${values.students.length} scholar${values.students.length > 1 ? 's' : ''}`);
        }

        queryClient.invalidateQueries({ queryKey: ['studentBehaviours'] });
        queryClient.invalidateQueries({ queryKey: ['streetCredMetrics'] });
        queryClient.invalidateQueries({ queryKey: ['student-financial-summary'] });
        queryClient.invalidateQueries({ queryKey: ['student'] });

        if (isDirectStudentMode && id) {
          navigate(getBaseRoute(`/view-student/${id}`));
        } else {
          navigate(getBaseRoute('/street-cred'));
        }
      } catch (err: any) {
        toast.error(err?.message || 'Failed to save behavior record');
      }
    },
  });

  // Pre-fill initial student if routed with direct scholar ID
  useEffect(() => {
    if (isDirectStudentMode && id && studentsData.length > 0 && formik.values.students.length === 0) {
      const matched = studentsData.find((s) => s.value === id);
      if (matched) {
        formik.setFieldValue('students', [matched]);
      }
    }
  }, [isDirectStudentMode, id, studentsData]);

  // Pre-fill existing data for edit mode
  useEffect(() => {
    if (isEditMode && existingBehaviour && studentsData.length > 0) {
      const studentId = existingBehaviour.studentId || (existingBehaviour.student as any)?.id;
      const matchedStudent = studentsData.find((s) => s.value === studentId);
      const studentOption: StudentOption = matchedStudent || {
        label: `${existingBehaviour.student?.user?.firstname || ''} ${existingBehaviour.student?.user?.lastname || ''}`.trim() || 'Scholar',
        value: studentId,
        email: existingBehaviour.student?.user?.email || '',
      };

      formik.setValues({
        students: [studentOption],
        behaviourTypeId: existingBehaviour.behaviourTypeId,
        notes: existingBehaviour.notes || '',
      });
    }
  }, [isEditMode, existingBehaviour, studentsData]);

  const selectedTypeObj = behaviourTypes.find((t) => t.id === formik.values.behaviourTypeId);
  const isHighlight = selectedTypeObj?.type === 'highlight';
  const selectedPoints =
    selectedTypeObj?.points !== 0 && selectedTypeObj?.points !== null && selectedTypeObj?.points !== undefined
      ? Number(selectedTypeObj.points)
      : isHighlight
      ? 10
      : -10;

  const handleCancel = () => {
    if (isDirectStudentMode && id) {
      navigate(getBaseRoute(`/view-student/${id}`));
    } else {
      navigate(getBaseRoute('/street-cred'));
    }
  };

  return (
    <Page title={isEditMode ? 'Edit Behavior | Gold Minds Portal' : 'Log Scholar Behavior | Gold Minds Portal'}>
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5 }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<Icon icon="solar:arrow-left-linear" width={16} />}
            onClick={handleCancel}
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.75rem',
              mb: 0.5,
              textTransform: 'none',
              px: 0.5,
              '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
            }}
          >
            {isDirectStudentMode ? 'Back to Scholar Profile' : 'Back to S.T.R.E.E.T Cred Hub'}
          </Button>

          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.25rem', lineHeight: 1.2 }}>
            {isEditMode ? 'Edit Behavior Record' : 'Log Scholar Behavior & Points'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            Recognize positive character milestones or log demerits with automatic point adjustments.
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            {/* Form Fields Card */}
            <Grid item xs={12} md={7.5}>
              <Card
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Stack spacing={2}>
                  {/* Scholar Selection */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8125rem' }}>
                      Select Scholar{isEditMode ? '' : '(s)'} *
                    </Typography>
                    <Autocomplete
                      multiple={!isEditMode}
                      options={studentsData}
                      loading={isLoadingStudents}
                      value={isEditMode ? formik.values.students[0] || null : formik.values.students}
                      onChange={(_, newValue) => {
                        if (isEditMode) {
                          formik.setFieldValue('students', newValue ? [newValue] : []);
                        } else {
                          formik.setFieldValue('students', newValue as StudentOption[]);
                        }
                      }}
                      getOptionLabel={(option) => option.label || ''}
                      isOptionEqualToValue={(option, val) => option.value === val.value}
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          size="small"
                          placeholder={formik.values.students.length > 0 ? '' : 'Search and select scholars...'}
                          error={Boolean(formik.touched.students && formik.errors.students)}
                          helperText={formik.touched.students && (formik.errors.students as string)}
                          InputProps={{
                            ...params.InputProps,
                            sx: { borderRadius: 1.5, fontSize: '0.8125rem' },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.value}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 700 }}>
                              {option.label.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                                {option.label}
                              </Typography>
                              {option.email && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                                  {option.email} {option.grade ? `· ${option.grade}` : ''}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      )}
                    />
                  </Box>

                  {/* Behavior Type Selection */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8125rem' }}>
                      Behavior Category & Points *
                    </Typography>
                    <FormControl fullWidth size="small" error={Boolean(formik.touched.behaviourTypeId && formik.errors.behaviourTypeId)}>
                      <MuiSelect
                        value={formik.values.behaviourTypeId}
                        onChange={(e) => formik.setFieldValue('behaviourTypeId', e.target.value)}
                        displayEmpty
                        sx={{ borderRadius: 1.5, fontSize: '0.8125rem' }}
                      >
                        <MenuItem value="" disabled>
                          <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>
                            -- Choose a behavior type --
                          </Typography>
                        </MenuItem>
                        {behaviourTypes.map((type) => {
                          const isHigh = type.type === 'highlight';
                          const pts =
                            type.points !== 0 && type.points !== null && type.points !== undefined
                              ? Number(type.points)
                              : isHigh
                              ? 10
                              : -10;
                          return (
                            <MenuItem key={type.id} value={type.id}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 0.75,
                                      bgcolor: isHigh ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                      color: isHigh ? '#059669' : '#DC2626',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Icon icon={isHigh ? 'solar:star-bold' : 'solar:shield-warning-bold'} width={12} />
                                  </Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                                    {type.title}
                                  </Typography>
                                </Stack>
                                <Chip
                                  label={`${pts >= 0 ? '+' : ''}${pts} pts`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.6875rem',
                                    fontWeight: 800,
                                    bgcolor: isHigh ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                    color: isHigh ? '#059669' : '#DC2626',
                                  }}
                                />
                              </Stack>
                            </MenuItem>
                          );
                        })}
                      </MuiSelect>
                      {formik.touched.behaviourTypeId && formik.errors.behaviourTypeId && (
                        <FormHelperText error>{formik.errors.behaviourTypeId}</FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {/* Notes & Description */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75, fontSize: '0.8125rem' }}>
                      Context & Incident Notes *
                    </Typography>
                    <MuiTextField
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      placeholder="Enter specific details, observations, or praise for this behavior..."
                      value={formik.values.notes}
                      onChange={(e) => formik.setFieldValue('notes', e.target.value)}
                      error={Boolean(formik.touched.notes && formik.errors.notes)}
                      helperText={formik.touched.notes && formik.errors.notes}
                      InputProps={{
                        sx: { borderRadius: 1.5, fontSize: '0.8125rem' },
                      }}
                    />
                  </Box>

                  {/* Actions */}
                  <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCancel}
                      disabled={formik.isSubmitting}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        height: 38,
                        borderColor: 'divider',
                        color: 'text.secondary',
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      disabled={formik.isSubmitting}
                      startIcon={formik.isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Icon icon="solar:check-read-bold" width={16} />}
                      sx={{
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 3,
                        height: 38,
                        boxShadow: 'none',
                      }}
                    >
                      {formik.isSubmitting ? 'Saving...' : isEditMode ? 'Update Record' : 'Confirm & Log Behavior'}
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            </Grid>

            {/* Live Preview Card */}
            <Grid item xs={12} md={4.5}>
              <Card
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  position: 'sticky',
                  top: 20,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5, display: 'block', fontSize: '0.6875rem' }}>
                  Live Recognition Preview
                </Typography>

                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: selectedTypeObj ? (isHighlight ? '#A7F3D0' : '#FECACA') : 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  {/* Scholar Summary */}
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: isHighlight ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: isHighlight ? '#059669' : '#DC2626',
                        fontWeight: 800,
                        fontSize: '0.875rem',
                      }}
                    >
                      {formik.values.students[0]?.label?.charAt(0) || 'S'}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.875rem', lineHeight: 1.2 }}>
                        {formik.values.students.length === 0
                          ? 'Scholar Name'
                          : formik.values.students.length === 1
                          ? formik.values.students[0].label
                          : `${formik.values.students[0].label} + ${formik.values.students.length - 1} other${formik.values.students.length > 2 ? 's' : ''}`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                        {formik.values.students[0]?.email || 'scholar@school.org'}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Selected Behavior Box */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: selectedTypeObj ? (isHighlight ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.06)') : 'grey.100',
                      border: '1px solid',
                      borderColor: selectedTypeObj ? (isHighlight ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)') : 'transparent',
                      mb: 1.5,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                        {selectedTypeObj?.type ? `${selectedTypeObj.type} Record` : 'Behavior Status'}
                      </Typography>
                      <Chip
                        label={`${selectedPoints >= 0 ? '+' : ''}${selectedPoints} pts`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.75rem',
                          fontWeight: 800,
                          bgcolor: isHighlight ? '#059669' : '#DC2626',
                          color: '#FFFFFF',
                        }}
                      />
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.875rem' }}>
                      {selectedTypeObj?.title || 'No behavior selected yet'}
                    </Typography>
                  </Box>

                  {/* Notes Preview */}
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', fontStyle: formik.values.notes ? 'normal' : 'italic' }}>
                    "{formik.values.notes || 'Incident or praise notes will be previewed here...'}"
                  </Typography>

                  <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6875rem' }}>
                      Date: {moment().format('MMM DD, YYYY')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6875rem' }}>
                      Status: Active
                    </Typography>
                  </Box>
                </Card>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Container>
    </Page>
  );
}
